import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { RiShieldKeyholeLine } from '@/shared/icons/remix'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { ApiError } from '@/api/http/types'
import {
  useResendPasswordResetOtp,
  useVerifyPasswordResetOtp,
} from '../api'
import {
  PASSWORD_RESET_TOKEN_STATE_KEY,
  readForgotPasswordEmailFromState,
  SP_OTP_EXPIRED_MESSAGE,
} from '../lib'
import { lockoutFromApiError } from '../lib/lockout'
import {
  AuthCard,
  BackToLoginButton,
  LockoutCountdown,
  OtpInput,
} from '../components'
import { useLockoutRemaining } from '../hooks/useLockoutRemaining'
import { headingSx, primaryButtonSx, subtitleSx } from '../styles/authFormStyles'
import { palettePrimaryMain } from '@/theme/designTokens'

const RESEND_COOLDOWN_SECONDS = 120

export function VerifyOtpPage() {
  useDocumentTitle('Verify Identity')
  const navigate = useNavigate()
  const location = useLocation()
  const email = readForgotPasswordEmailFromState(location.state)

  const verifyOtp = useVerifyPasswordResetOtp()
  const resendOtp = useResendPasswordResetOtp()

  const [code, setCode] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)
  const [lockoutUntilMs, setLockoutUntilMs] = useState<number | null>(null)
  const [resendCooldownUntilMs, setResendCooldownUntilMs] = useState<number | null>(
    null
  )
  const lockoutRemaining = useLockoutRemaining(lockoutUntilMs)
  const resendRemaining = useLockoutRemaining(resendCooldownUntilMs)
  const isLockedOut = lockoutRemaining > 0
  const resendDisabled = resendRemaining > 0 || resendOtp.isPending || isLockedOut

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password', { replace: true })
    }
  }, [email, navigate])

  if (!email) return null

  const handleVerify = () => {
    if (code.length !== 6 || isLockedOut) return
    setServerError(null)
    verifyOtp.mutate(
      { email, code },
      {
        onSuccess: (data) => {
          if (!data?.resetToken) {
            setServerError('Verification failed. Please try again.')
            return
          }
          navigate('/reset-password', {
            replace: true,
            state: { [PASSWORD_RESET_TOKEN_STATE_KEY]: data.resetToken },
          })
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            const lockout = lockoutFromApiError(error)
            if (lockout) {
              setLockoutUntilMs(lockout.lockedUntilMs)
              setServerError(error.message)
              return
            }
            if (error.status === 410) {
              setServerError(SP_OTP_EXPIRED_MESSAGE)
              return
            }
            setServerError(error.message)
            return
          }
          setServerError('An unexpected error occurred. Please try again.')
        },
      }
    )
  }

  const handleResend = () => {
    if (resendDisabled) return
    setServerError(null)
    resendOtp.mutate(
      { email },
      {
        onSuccess: () => {
          setCode('')
          setResendCooldownUntilMs(Date.now() + RESEND_COOLDOWN_SECONDS * 1000)
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            const lockout = lockoutFromApiError(error)
            if (lockout?.retryAfterSeconds) {
              setResendCooldownUntilMs(
                Date.now() + lockout.retryAfterSeconds * 1000
              )
              setServerError(error.message)
              return
            }
            setServerError(error.message)
          }
        },
      }
    )
  }

  return (
    <AuthCard>
      <Box
        sx={{
          width: 64,
          height: 64,
          bgcolor: '#e6f5fc',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RiShieldKeyholeLine size={32} color={palettePrimaryMain} aria-hidden />
      </Box>

      <Typography component="h1" sx={{ ...headingSx, mt: '22px' }}>
        Verify Identity
      </Typography>
      <Typography sx={{ ...subtitleSx, mt: '6px' }}>
        Enter the 6-digit code sent to {email}
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mt: 2, width: '100%' }} role="alert">
          {serverError}
        </Alert>
      )}

      {lockoutUntilMs && (
        <LockoutCountdown
          lockedUntilMs={lockoutUntilMs}
          message="Too many incorrect attempts. Please wait before trying again."
        />
      )}

      <Box sx={{ mt: 3, width: '100%' }}>
        <OtpInput
          value={code}
          onChange={setCode}
          disabled={isLockedOut || verifyOtp.isPending}
          error={!!serverError && !isLockedOut}
        />
      </Box>

      <Button
        type="button"
        variant="contained"
        fullWidth
        disableElevation
        disabled={code.length !== 6 || isLockedOut || verifyOtp.isPending}
        onClick={handleVerify}
        sx={{ ...primaryButtonSx, mt: 3 }}
      >
        {verifyOtp.isPending ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Verify'
        )}
      </Button>

      <Button
        type="button"
        variant="text"
        onClick={handleResend}
        disabled={resendDisabled}
        sx={{ mt: 2, textTransform: 'none', color: palettePrimaryMain }}
      >
        {resendRemaining > 0
          ? `Resend code in ${resendRemaining}s`
          : 'Resend code'}
      </Button>

      <BackToLoginButton />
    </AuthCard>
  )
}
